/**
 * 知识库 API 服务层
 * 调用后端 RESTful API，接口与 knowledgeStorage.js 保持一致
 */
import axios from 'axios'

const API_URL = process.env.VUE_APP_API_URL || '/api'
const KB_API = `${API_URL}/knowledge`

// ---- Workspace Storage ----
export const workspaceStorage = {
  async getAll() {
    const res = await axios.get(`${KB_API}/workspaces`)
    return res.data
  },

  async getById(workspaceId) {
    try {
      const res = await axios.get(`${KB_API}/workspaces/${workspaceId}`)
      return res.data
    } catch (err) {
      if (err.response?.status === 404) return null
      throw err
    }
  },

  async create(workspace) {
    const res = await axios.post(`${KB_API}/workspaces`, workspace)
    return res.data
  },

  async update(workspaceId, updates) {
    const res = await axios.put(`${KB_API}/workspaces/${workspaceId}`, updates)
    return res.data
  },

  async remove(workspaceId) {
    await axios.delete(`${KB_API}/workspaces/${workspaceId}`)
  }
}

// ---- Node Storage ----
export const nodeStorage = {
  async getByWorkspace(workspaceId) {
    const res = await axios.get(`${KB_API}/nodes`, { params: { workspaceId } })
    return res.data
  },

  async create(node, document = null) {
    const payload = { ...node }
    if (document) payload.document = document
    const res = await axios.post(`${KB_API}/nodes`, payload)
    return res.data.node || res.data
  },

  async update(nodeId, updates) {
    const res = await axios.put(`${KB_API}/nodes/${nodeId}`, updates)
    return res.data
  },

  async remove(nodeId, workspaceId) {
    await axios.delete(`${KB_API}/nodes/${nodeId}`, { params: { workspaceId } })
  },

  async move(nodeId, newParentId, newOrder) {
    const res = await axios.put(`${KB_API}/nodes/${nodeId}`, {
      parent_id: newParentId,
      order: newOrder
    })
    return res.data
  }
}

// ---- Document Storage ----
export const documentStorage = {
  async getById(documentId) {
    try {
      const res = await axios.get(`${KB_API}/documents/${documentId}`)
      return res.data
    } catch (err) {
      if (err.response?.status === 404) return null
      throw err
    }
  },

  async getByNodeId(nodeId) {
    try {
      const res = await axios.get(`${KB_API}/documents/by-node/${nodeId}`)
      return res.data
    } catch (err) {
      if (err.response?.status === 404) return null
      throw err
    }
  },

  async create(document) {
    // 文档通常在创建节点时一起创建，这里提供独立创建接口作为后备
    const res = await axios.post(`${KB_API}/nodes`, {
      ...document,
      type: 'document'
    })
    return res.data.document || res.data
  },

  async update(documentId, updates) {
    const res = await axios.put(`${KB_API}/documents/${documentId}`, updates)
    return res.data
  },

  async remove(documentId) {
    // 文档随节点删除，一般不单独调用
    await axios.delete(`${KB_API}/documents/${documentId}`)
  }
}

// ---- Canvas Storage ----
export const canvasStorage = {
  async getComponents(workspaceId) {
    const res = await axios.get(`${KB_API}/canvas/${workspaceId}`)
    return res.data
  },

  async addComponent(component) {
    const res = await axios.post(`${KB_API}/canvas`, component)
    return res.data
  },

  async updateComponent(componentId, updates) {
    const res = await axios.put(`${KB_API}/canvas/component/${componentId}`, updates)
    return res.data
  },

  async removeComponent(componentId) {
    await axios.delete(`${KB_API}/canvas/component/${componentId}`)
  },

  // 画布视图状态暂存在前端（可后续扩展到服务端）
  async getState(workspaceId) {
    try {
      const data = localStorage.getItem(`kb_canvas_state_${workspaceId}`)
      return data ? JSON.parse(data) : null
    } catch {
      return null
    }
  },

  async saveState(state) {
    localStorage.setItem(`kb_canvas_state_${state.workspace_id}`, JSON.stringify(state))
    return state
  }
}
